import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, Share2 } from "lucide-react";
import { useCaps } from "../editor/CapsProvider";
import { useShareDesign } from "./useShareDesign";
import { EASE_OUT } from "../theme";

/**
 * Floating Share button, bottom-right of the viewer stage — the counterweight
 * to the AI sparkle FAB bottom-left.
 *
 * Not a second implementation of anything: it runs `useShareDesign`, the same
 * flow behind the ActionBar's Save Design and Share Link. The footer keeps
 * those entries (they live with Save / Load, where someone goes looking for
 * them); this is the one-tap version for someone who has just finished the ring
 * and is looking at it, not at the panel.
 *
 * Hidden on a pre-v37 viewer for the same reason the footer's entries are
 * disabled there: `serializeEngagementDesign` has nothing to serialize.
 */
export function ShareFab() {
  const caps = useCaps();
  const { share, busy } = useShareDesign();
  const [note, setNote] = useState<string | null>(null);

  if (!caps.ringBuilderV37) return null;

  const onClick = () => {
    if (busy) return;
    void (async () => {
      try {
        const result = await share();
        setNote(result.mode === "vault" ? "Link copied" : "Saved locally — link copied");
      } catch (err) {
        setNote(`Could not share: ${(err as Error).message || "unknown error"}`);
      }
      setTimeout(() => setNote(null), 2600);
    })();
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
      <AnimatePresence>
        {note ? (
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: EASE_OUT }}
            role="status"
            className="flex items-center gap-1.5 rounded-full border border-outline-variant/40 bg-surface px-3.5 py-2 font-body text-[11px] text-primary shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
          >
            <Check size={13} />
            {note}
          </motion.span>
        ) : null}
      </AnimatePresence>

      <motion.button
        type="button"
        data-testid="ring-share-fab"
        title="Save and copy a link to this design"
        aria-label="Save and copy a link to this design"
        disabled={busy}
        onClick={onClick}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-on-primary shadow-[0_10px_30px_rgba(0,0,0,0.18)] transition-colors hover:bg-surface-tint disabled:opacity-60"
      >
        {busy ? <Loader2 size={19} className="animate-spin" /> : <Share2 size={18} />}
      </motion.button>
    </div>
  );
}
